<template>
  <div
    class="sticky bottom-0 z-10 flex flex-col gap-1 border-t border-primary-400 bg-primary-800 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))]"
    :class="reducedSidebar ? 'xl:px-2' : 'xl:px-4'"
  >
    <div v-if="reducedSidebar" class="flex justify-center">
      <dropdown-button fixed menu-class="bottom-4 left-16">
        <template #button>
          <sidebar-footer-button
            icon="fa-user-circle"
            :label="$t('label.more')"
            data-cy="sidebar-more"
          />
        </template>
        <button-menu-item
          v-for="action in actions"
          :key="action.testId"
          @click="go(action)"
        >
          <span class="flex items-center gap-2" :data-cy="action.testId">
            <i class="far w-4" :class="action.icon"></i>
            {{ action.label }}
          </span>
        </button-menu-item>
        <div class="my-1 border-t border-gray-100"></div>
        <button-menu-item
          v-for="language in LANGUAGES"
          :key="language.code"
          :selected="locale === language.code"
          @click="setLanguage(language.code)"
        >
          {{ language.label }}
        </button-menu-item>
      </dropdown-button>
    </div>

    <div
      v-else
      class="flex items-center gap-2"
      :class="myAccountAction ? 'justify-between' : 'justify-end'"
    >
      <div v-if="myAccountAction" class="flex min-w-0 items-center gap-2">
        <sidebar-footer-button
          :icon="myAccountAction.icon"
          :label="myAccountAction.label"
          :to="myAccountAction.to"
          :data-cy="myAccountAction.testId"
        />
        <span
          class="truncate text-sm text-primary-100"
          data-cy="sidebar-current-employee"
          >{{ loggedInUserName }}</span
        >
      </div>

      <dropdown-button left top>
        <template #button>
          <sidebar-footer-button
            icon="fa-globe"
            :label="$t('navbar.language')"
            data-cy="sidebar-language"
          />
        </template>
        <button-menu-item
          v-for="language in LANGUAGES"
          :key="language.code"
          :selected="locale === language.code"
          @click="setLanguage(language.code)"
        >
          {{ language.label }}
        </button-menu-item>
      </dropdown-button>
    </div>

    <div
      v-if="!reducedSidebar || canSwitchEmployee"
      class="flex items-center gap-2"
      :class="reducedSidebar ? 'justify-center' : 'justify-between'"
    >
      <div class="flex items-center gap-1">
        <sidebar-footer-button
          v-if="canSwitchEmployee"
          icon="fa-people-arrows"
          :label="$t('deviceSessions.switch')"
          data-cy="sidebar-switch-employee"
          @click="switchEmployee()"
        />

        <sidebar-footer-button
          v-if="spaceAction && !reducedSidebar"
          :icon="spaceAction.icon"
          :label="spaceAction.label"
          :to="spaceAction.to"
          :data-cy="spaceAction.testId"
        />
      </div>

      <sidebar-footer-button
        v-if="logoutAction && !reducedSidebar"
        :icon="logoutAction.icon"
        :label="logoutAction.label"
        :to="logoutAction.to"
        :data-cy="logoutAction.testId"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouteLocationRaw, useRouter } from 'vue-router';
import DropdownButton from '@/components/shared/dropdowns/DropdownButton.vue';
import ButtonMenuItem from '@/components/shared/dropdowns/ButtonMenuItem.vue';
import SidebarFooterButton from '@/components/shared/SidebarFooterButton.vue';
import i18n, { Lang } from '@/config/i18n';
import { useUiStore } from '@/store/ui-store';
import { useUsersStore } from '@/store/users-store';

interface FooterAction {
  testId: string;
  icon: string;
  label: string;
  to: RouteLocationRaw;
}

const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

const router = useRouter();
const uiStore = useUiStore();
const usersStore = useUsersStore();

const reducedSidebar = computed(() => uiStore.reducedSidebar);
const canSwitchEmployee = computed(() => usersStore.canSwitchEmployee);
const locale = computed(() => i18n.global.locale.value);
const isStaffOrMore = computed(() => usersStore.isStaffOrMore);
const loggedInUserName = computed(() => usersStore.loggedInUserName);
const hasSeveralSpaces = computed(() => usersStore.hasSeveralSpaces);

const myAccountAction = computed((): FooterAction | null =>
  isStaffOrMore.value
    ? {
        testId: 'sidebar-my-account',
        icon: 'fa-user-circle',
        label: i18n.global.t('navbar.myAccount'),
        to: { name: 'MyAccountEmployeeEdit' },
      }
    : null,
);

const logoutAction = computed((): FooterAction | null =>
  isStaffOrMore.value
    ? {
        testId: 'sidebar-logout',
        icon: 'fa-sign-out',
        label: i18n.global.t('navbar.logout'),
        to: { name: 'Logout' },
      }
    : null,
);

const spaceAction = computed((): FooterAction | null =>
  hasSeveralSpaces.value
    ? {
        testId: 'sidebar-switch-space',
        icon: 'fa-exchange',
        label: i18n.global.t('spaces.switch'),
        to: { name: 'Spaces' },
      }
    : null,
);

const actions = computed((): FooterAction[] =>
  [myAccountAction.value, logoutAction.value, spaceAction.value].filter(
    (action): action is FooterAction => action !== null,
  ),
);

function switchEmployee() {
  usersStore.parkSession();
}

function go(action: FooterAction) {
  router.push(action.to);
}

function setLanguage(language: Lang) {
  usersStore.setLanguage(language);
}
</script>
